/**
 * Plan del completivo del inicial (ver lib/completivoplan):
 *  - PlanCompletivoEditor: en la venta a crédito, reparte el completivo en
 *    partes con fecha (atajo "Repartir" + edición fila por fila).
 *  - PlanCompletivoResumen: en el cobro, vencido / por vencer / próximo y el
 *    estado de cada parte.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { FP, fpInputSx } from "components/financepro";
import {
  FRECUENCIAS_COMPLETIVO,
  repartirCompletivo,
  sumarPeriodo,
  sumaPlan,
  hoyISO,
  fechaCorta,
} from "lib/completivoplan";

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const chip = (bg, color) => ({
  px: 1,
  py: 0.25,
  borderRadius: 1.2,
  fontSize: "0.72rem",
  fontWeight: 700,
  bgcolor: bg,
  color,
  whiteSpace: "nowrap",
});

const btnOutline = (c) => ({
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 2,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

// Primera fecha sugerida: un mes después de hoy.
const sugerirPrimeraFecha = () => sumarPeriodo(hoyISO(), "Meses", 1);

// `fechaMinima` ('YYYY-MM-DD'): piso del selector de fechas; por defecto hoy.
export function PlanCompletivoEditor({ completivo, plan, onChange, fechaMinima }) {
  const minFecha = fechaMinima || hoyISO();
  const [partes, setPartes] = useState("2");
  const [primera, setPrimera] = useState(sugerirPrimeraFecha);
  const [frecuencia, setFrecuencia] = useState("Meses");

  const total = Math.round((Number(completivo) || 0) * 100) / 100;
  const suma = sumaPlan(plan);
  const dif = Math.round((total - suma) * 100) / 100;
  const cuadra = Math.abs(dif) < 0.01;

  const repartir = () => onChange(repartirCompletivo(total, partes, primera, frecuencia));
  const cambiar = (idx, k, v) => onChange(plan.map((p, i) => (i === idx ? { ...p, [k]: v } : p)));
  const quitar = (idx) => onChange(plan.filter((_, i) => i !== idx));
  const agregar = () => {
    const ultima = plan[plan.length - 1];
    const fecha = ultima?.fecha ? sumarPeriodo(ultima.fecha, frecuencia, 1) : primera;
    onChange([...plan, { fecha, monto: dif > 0 ? dif : "" }]);
  };

  let estado;
  if (!plan.length) {
    estado = (
      <Box component="span" sx={chip(FP.color.surfaceAlt, FP.color.textSecondary)}>
        Sin plan: se cobra todo de una vez
      </Box>
    );
  } else if (cuadra) {
    estado = (
      <Box component="span" sx={chip(FP.color.successSoft, FP.color.successDark)}>
        Cuadra con el completivo
      </Box>
    );
  } else {
    estado = (
      <Box component="span" sx={chip(FP.color.dangerSoft, FP.color.danger)}>
        {dif > 0 ? "Faltan" : "Sobran"} RD$ {fmtMoney(Math.abs(dif))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${FP.color.accentBlue}55`,
        borderRadius: 2,
        p: { xs: 1.6, sm: 2 },
        bgcolor: `${FP.color.accentBlueSoft}55`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.6 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <EventNoteOutlinedIcon sx={{ color: FP.color.accentBlue }} fontSize="small" />
          <Typography sx={{ fontWeight: 800, fontSize: "0.92rem", color: FP.color.textPrimary }}>
            Plan del completivo · RD$ {fmtMoney(total)}
          </Typography>
        </Stack>
        {estado}
      </Stack>

      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs={5} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Cantidad de pagos"
            value={partes}
            onChange={(e) => setPartes(e.target.value)}
            inputProps={{ min: 1, max: 24 }}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={7} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Primera fecha"
            value={primera}
            onChange={(e) => setPrimera(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minFecha }}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={7} sm={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Frecuencia"
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value)}
            sx={fpInputSx}
          >
            {FRECUENCIAS_COMPLETIVO.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={5} sm={3}>
          <Button
            fullWidth
            startIcon={<AutorenewIcon />}
            onClick={repartir}
            sx={{ ...btnOutline(FP.color.accentBlue), height: 40 }}
          >
            Repartir
          </Button>
        </Grid>
      </Grid>

      {plan.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.8 }}>
          {plan.map((p, idx) => (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              direction="row"
              alignItems="center"
              spacing={1.2}
            >
              <Typography
                sx={{
                  width: 22,
                  flexShrink: 0,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: FP.color.textSecondary,
                }}
              >
                {idx + 1}
              </Typography>
              <TextField
                size="small"
                type="date"
                label="Fecha"
                value={p.fecha}
                onChange={(e) => cambiar(idx, "fecha", e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minFecha }}
                sx={{ ...fpInputSx, flex: 1, minWidth: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="Monto"
                value={p.monto}
                onChange={(e) => cambiar(idx, "monto", e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                sx={{ ...fpInputSx, flex: 1, minWidth: 0 }}
              />
              <IconButton
                size="small"
                aria-label={`Quitar pago ${idx + 1}`}
                onClick={() => quitar(idx)}
                sx={{ color: FP.color.danger }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mt: 1.4 }}
      >
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={agregar}
          sx={{ textTransform: "none", fontWeight: 700, color: FP.color.accentBlue }}
        >
          Agregar pago
        </Button>
        <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
          {plan.length
            ? `${plan.length} pago${plan.length === 1 ? "" : "s"} · suma RD$ ${fmtMoney(suma)}`
            : "Sin plan, el completivo vence el día de la venta"}
        </Typography>
      </Stack>
    </Box>
  );
}

const ESTADOS = {
  pagada: { label: "Pagada", bg: FP.color.successSoft, color: FP.color.successDark },
  vencida: { label: "Vencida", bg: FP.color.dangerSoft, color: FP.color.danger },
  porvencer: { label: "Por vencer", bg: FP.color.surfaceAlt, color: FP.color.textSecondary },
};

function Cifra({ label, value, color }) {
  return (
    <Box
      sx={{ bgcolor: FP.color.surfaceAlt, borderRadius: 1.6, px: 1.4, py: 1, flex: 1, minWidth: 0 }}
    >
      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textSecondary }}>{label}</Typography>
      <Typography
        sx={{ fontSize: "0.98rem", fontWeight: 800, color: color || FP.color.textPrimary }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// `estado` es el resultado de estadoPlanCompletivo.
export function PlanCompletivoResumen({ estado }) {
  if (!estado || !estado.partes.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Cifra
          label="Vencido"
          value={`RD$ ${fmtMoney(estado.vencido)}`}
          color={estado.vencido > 0 ? FP.color.danger : undefined}
        />
        <Cifra label="Por vencer" value={`RD$ ${fmtMoney(estado.porVencer)}`} />
        <Cifra label="Próximo" value={estado.proximo ? fechaCorta(estado.proximo) : "—"} />
      </Stack>
      <Stack spacing={0.5}>
        {estado.partes.map((p) => {
          const e = ESTADOS[p.estado];
          return (
            <Stack
              key={p.nro}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.2, py: 0.5, borderRadius: 1.4, border: `1px solid ${FP.color.border}` }}
            >
              <Typography sx={{ width: 18, fontSize: "0.78rem", color: FP.color.textSecondary }}>
                {p.nro}
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", flex: 1 }}>{fechaCorta(p.fecha)}</Typography>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                RD$ {fmtMoney(p.estado === "pagada" ? p.monto : p.pendiente)}
                {p.pagado > 0 && p.estado !== "pagada" ? (
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: FP.color.textMuted, ml: 0.6 }}
                  >
                    de {fmtMoney(p.monto)}
                  </Box>
                ) : null}
              </Typography>
              <Box component="span" sx={chip(e.bg, e.color)}>
                {e.label}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
